import { DeleteOutlined } from "@ant-design/icons";
import * as Yup from "yup";

import { Avatar, Button, Empty, Flex, Input, List } from "antd";
import { useFormik } from "formik";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { useCreateOnlineOrder2 } from "../../api/api.post";
import { useNotifications2 } from "../../components/notifications/notification";
import { CardPaymentForm } from "../../components/payment-forms/card-payment.form";
import { RadioGroupComponent } from "../../components/radio.group.component";
import { useStoreContext } from "../../providers/store.provider";
import {
  AUTHORIZATION_TEMPLATE_CONSENT_ID,
  ICardDetails,
  IProductForScanOrder,
  ONLINE_ORDER_FULLFILMENT_TYPE,
  ONLINE_ORDER_TYPE,
  PAYMENT_TYPE,
} from "../../types/types";
import { IOnlineCheckoutProps } from "./checkout.props";
import { toFixed, toFixedDisplay } from "../../utils/number.utils";
import {
  calculateItemTotal,
  calculateProductTotal,
  calculateTaxtotal,
} from "../../utils/product.utils";
import { useGetOnlineFees } from "../../api/api.get";
import { FeesTable } from "../../components/fees.table";
import { processFeesOnly } from "../../utils/fee.utils";

export default function OnlineDeliveryCheckout({
  onOK,
  storeDetails,
  items,
  onRemoveItems,
}: IOnlineCheckoutProps) {
  const { id } = useParams();
  const { currency, phonePrefix } = useStoreContext();

  const { data: feesData } = useGetOnlineFees(id);

  const fees = useMemo(() => feesData ?? [], [feesData]);

  const grossTotal = useMemo(
    () => calculateProductTotal(items, storeDetails?.tax, null, fees),
    [items, storeDetails, fees],
  );

  const { mutateAsync, isPending } = useCreateOnlineOrder2(id);

  const { notifyError, notifySuccess, notification } = useNotifications2();

  const [itemsToCheckout, setItemsToCheckout] = useState<
    IProductForScanOrder[]
  >([]);

  useEffect(() => {
    if (items) {
      setItemsToCheckout(
        items.map((item) => {
          return {
            ...item,
            uniqueId: uuidv4(),
          };
        }),
      );
    }
  }, [items]);

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      street1: "",
      street2: "",
      city: "",
      state: "",
      phone: "",
      country: storeDetails?.country,
      zipcode: "",
      address: "",
      paymentType: "",
      fullfilmentType: "",
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      firstName: Yup.string().required("Please enter First Name"),
      lastName: Yup.string().required("Please enter Last Name"),
      phone: Yup.string().required("Please enter phone"),
      email: Yup.string().email("Invalid email format"),
      fullfilmentType: Yup.string().required(
        "Please select delivery or store pickup",
      ),
      street1: Yup.string().when("fullfilmentType", {
        is: ONLINE_ORDER_FULLFILMENT_TYPE.DELIVERY,
        then: (schema) => schema.required("Please enter street"),
      }),
      city: Yup.string().when("fullfilmentType", {
        is: ONLINE_ORDER_FULLFILMENT_TYPE.DELIVERY,
        then: (schema) => schema.required("Please enter city"),
      }),
      state: Yup.string().when("fullfilmentType", {
        is: ONLINE_ORDER_FULLFILMENT_TYPE.DELIVERY,
        then: (schema) => schema.required("Please enter state"),
      }),
      zipcode: Yup.string().when("fullfilmentType", {
        is: ONLINE_ORDER_FULLFILMENT_TYPE.DELIVERY,
        then: (schema) => schema.required("Please enter zipcode"),
      }),
    }),
    onSubmit: async (values) => {
      // for USA, verify if card token is present for online payment type.
      if (
        values.country === "USA" &&
        values.paymentType === PAYMENT_TYPE.CARD_ONLINE
      ) {
        if (!acceptTerms) {
          notifyError("Please accept terms");
          return;
        }

        if (!cardDetails) {
          notifyError("Please enter payment details");
          return;
        }

        if (!cardDetails.cardToken) {
          notifyError("Please enter payment info details");
          return;
        }

        if (!cardDetails.expiry) {
          notifyError("Please enter card expiry");
          return;
        }
      }

      if (!values.fullfilmentType) {
        notifyError("Please select delivery or pickup");
        return;
      }

      mutateAsync({
        products: itemsToCheckout,
        amount: toFixed(itemsTotal),
        tax: toFixed(storeDetails?.tax),
        total: toFixed(grossTotal),
        paymentType:
          values.country === "IND"
            ? PAYMENT_TYPE.OTHER
            : (formik.values.paymentType as PAYMENT_TYPE),
        type: ONLINE_ORDER_TYPE.NONE, // this is plain online
        customer: {
          country: storeDetails?.country, // customer is always same as store.
          firstName: values.firstName,
          lastName: values.lastName,
          phone: `${phonePrefix}${values.phone}`,
          city: values.city,
          state: values.state,
          street1: values.street1,
          street2: values.street2,
          zipcode: values.zipcode,
          email: values.email,
        },
        fullfilmentType:
          values.fullfilmentType as ONLINE_ORDER_FULLFILMENT_TYPE,
        authConsentTemplateId:
          AUTHORIZATION_TEMPLATE_CONSENT_ID.CUSTOMER_PAY_INVOICE_ONLINE_CARD,
        nonce: uuidv4(),
        zipCode: values.zipcode,
        cardDetails,
      })
        .then(() => {
          formik.resetForm();
          notifySuccess({
            title: "Success",
            description: "Order successfully placed.",
          });
          onOK();
        })
        .catch((err) => {
          if (err.status === 401) {
            notifyError("Login code expired. Please enter new one.");
          } else if (err.status === 403) {
            notifyError("Please enter new login.");
          } else {
            notifyError("Whoops! Something went wrong.");
          }
        });
    },
  });

  const itemsTotal = useMemo(() => {
    return calculateItemTotal(itemsToCheckout);
  }, [itemsToCheckout]);

  const taxTotal = useMemo(() => {
    if (storeDetails?.tax) {
      return calculateTaxtotal(itemsToCheckout, +storeDetails.tax);
    }
    return 0;
  }, [storeDetails?.tax, itemsToCheckout]);

  // fees are charged off the item total, not the gross total.
  const feesTotal = useMemo(() => {
    return processFeesOnly(itemsTotal, fees);
  }, [itemsTotal, fees]);

  const [acceptTerms, setAcceptTerms] = useState<boolean>(false);

  const [cardDetails, setCardDetails] = useState<ICardDetails>();

  return (
    <>
      {notification}
      <div className="divColumn">
        <div className="my-4">
          {itemsToCheckout.length === 0 && <Empty description="No Items" />}
          {itemsToCheckout.length > 0 && (
            <Flex vertical gap={20}>
              <List
                dataSource={itemsToCheckout}
                itemLayout="horizontal"
                bordered
                renderItem={(item, index) => (
                  <RowItem
                    item={item}
                    key={index}
                    fontColor={storeDetails?.bodyFontColor}
                    onRemoveItems={(item) => onRemoveItems(item)}
                  />
                )}
              />

              <div className="my-4 flex flex-col">
                <div className="flex my-2 justify-between">
                  <span className="flex w-[100px] items-center">
                    Net Total:{" "}
                  </span>
                  <strong className="font-bold text-xl">
                    {currency}
                    {toFixedDisplay(itemsTotal)}
                  </strong>
                </div>

                <div className="flex my-2 justify-between">
                  <span className="flex w-[100px] items-center">
                    Tax: ({storeDetails?.tax ?? 0}%)
                  </span>
                  <strong className="font-bold text-xl">
                    {currency}
                    {toFixedDisplay(taxTotal)}
                  </strong>
                </div>

                {fees.length > 0 && (
                  <>
                    <FeesTable fees={fees} itemTotal={itemsTotal} />

                    <div className="flex my-2 justify-between">
                      <span className="flex w-[100px] items-center">Fees:</span>
                      <strong className="font-bold text-xl">
                        {currency}
                        {toFixedDisplay(feesTotal)}
                      </strong>
                    </div>
                  </>
                )}

                <div className="flex my-2 justify-between">
                  <span className="flex w-[100px] items-center">Total:</span>
                  <strong className="font-bold text-xl">
                    {currency}
                    {toFixedDisplay(grossTotal)}
                  </strong>
                </div>
              </div>

              <div>
                <Flex vertical gap={5}>
                  Enter Phone Number
                  <Flex>
                    <Input
                      placeholder="Phone number"
                      prefix={phonePrefix}
                      {...formik.getFieldProps("phone")}
                    />
                  </Flex>
                </Flex>

                {formik.values.phone.length >= 10 && (
                  <Flex vertical>
                    <Flex vertical gap={5} className="my-4">
                      First Name
                      <Input
                        placeholder="First Name"
                        {...formik.getFieldProps("firstName")}
                      />
                      {formik.touched.firstName && formik.errors.firstName && (
                        <div style={{ color: "red", fontSize: 12 }}>
                          {formik.errors.firstName}
                        </div>
                      )}
                    </Flex>

                    <Flex vertical gap={5} className="my-4">
                      Last Name
                      <Input
                        placeholder="Last Name"
                        {...formik.getFieldProps("lastName")}
                      />
                      {formik.touched.lastName && formik.errors.lastName && (
                        <div style={{ color: "red", fontSize: 12 }}>
                          {formik.errors.lastName}
                        </div>
                      )}
                    </Flex>

                    <Flex vertical gap={5} className="my-4">
                      Email
                      <Input
                        placeholder="Email"
                        {...formik.getFieldProps("email")}
                      />
                    </Flex>

                    <div className="my-5 flex flex-col gap-5">
                      {formik.values.country === "USA" && (
                        <RadioGroupComponent
                          defaultValue="card"
                          items={[
                            ...(storeDetails?.allowCard
                              ? [
                                  {
                                    label: "Card",
                                    value: PAYMENT_TYPE.CARD_ONLINE,
                                  },
                                ]
                              : []),
                            ...(storeDetails?.allowCash
                              ? [
                                  {
                                    label: "Cash",
                                    value: PAYMENT_TYPE.CASH,
                                  },
                                ]
                              : []),
                            ...(storeDetails?.allowApplePay
                              ? [
                                  {
                                    label: "Apple Pay",
                                    value: PAYMENT_TYPE.APPLE_PAY,
                                  },
                                ]
                              : []),
                          ]}
                          onChange={(value) => {
                            formik.setFieldValue("paymentType", value);
                          }}
                        />
                      )}

                      <RadioGroupComponent
                        defaultValue=""
                        items={[
                          ...(storeDetails?.allowOnlineDelivery
                            ? [
                                {
                                  label: "Delivery",
                                  value: ONLINE_ORDER_FULLFILMENT_TYPE.DELIVERY,
                                },
                              ]
                            : []),
                          ...(storeDetails?.allowOnlinePickup
                            ? [
                                {
                                  label: "Pickup",
                                  value: ONLINE_ORDER_FULLFILMENT_TYPE.PICKUP,
                                },
                              ]
                            : []),
                        ]}
                        onChange={(value) => {
                          formik.setFieldValue("fullfilmentType", value);
                        }}
                      />

                      {formik.values.fullfilmentType ===
                        ONLINE_ORDER_FULLFILMENT_TYPE.DELIVERY && (
                        <Flex vertical>
                          <Flex vertical gap={5} className="my-4">
                            Street 1
                            <Input
                              placeholder="Street address"
                              {...formik.getFieldProps("street1")}
                            />
                            {formik.touched.street1 &&
                              formik.errors.street1 && (
                                <div style={{ color: "red", fontSize: 12 }}>
                                  {formik.errors.street1}
                                </div>
                              )}
                          </Flex>

                          <Flex vertical gap={5} className="my-4">
                            Street 2
                            <Input
                              placeholder="Apartment, suite, etc. (optional)"
                              {...formik.getFieldProps("street2")}
                            />
                          </Flex>

                          <Flex vertical gap={5} className="my-4">
                            City
                            <Input
                              placeholder="City"
                              {...formik.getFieldProps("city")}
                            />
                            {formik.touched.city && formik.errors.city && (
                              <div style={{ color: "red", fontSize: 12 }}>
                                {formik.errors.city}
                              </div>
                            )}
                          </Flex>

                          <Flex vertical gap={5} className="my-4">
                            State
                            <Input
                              placeholder="State"
                              {...formik.getFieldProps("state")}
                            />
                            {formik.touched.state && formik.errors.state && (
                              <div style={{ color: "red", fontSize: 12 }}>
                                {formik.errors.state}
                              </div>
                            )}
                          </Flex>

                          <Flex vertical gap={5} className="my-4">
                            Zipcode
                            <Input
                              placeholder="Zipcode"
                              {...formik.getFieldProps("zipcode")}
                            />
                            {formik.touched.zipcode &&
                              formik.errors.zipcode && (
                                <div style={{ color: "red", fontSize: 12 }}>
                                  {formik.errors.zipcode}
                                </div>
                              )}
                          </Flex>
                        </Flex>
                      )}

                      {formik.values.country === "USA" &&
                        formik.values.paymentType ===
                          PAYMENT_TYPE.CARD_ONLINE && (
                          <div>
                            <CardPaymentForm
                              onSuccess={(cardDetails) =>
                                setCardDetails(cardDetails)
                              }
                              context="customer"
                              onChangeAcceptTerms={(value) =>
                                setAcceptTerms(value)
                              }
                              onChangeZipCode={(value) =>
                                formik.setFieldValue("zipcode", value)
                              }
                              onError={(message) => {}}
                            />
                          </div>
                        )}

                      {Object.keys(formik.errors).length > 0 && (
                        <Flex vertical gap={5}>
                          <span className="text-xs text-red-500">
                            {
                              (formik.errors as any)[
                                Object.keys(formik.errors)[0]
                              ]
                            }
                          </span>
                        </Flex>
                      )}
                      <Button
                        type="primary"
                        className="w-full"
                        disabled={Object.keys(formik.errors).length > 0}
                        onClick={() => formik.submitForm()}
                        loading={isPending}
                      >
                        {"Place Order"}
                      </Button>
                    </div>
                  </Flex>
                )}
              </div>
            </Flex>
          )}
        </div>
      </div>
    </>
  );
}

function RowItem({
  item,
  onRemoveItems,
  fontColor,
}: {
  item: IProductForScanOrder;
  onRemoveItems: (item: IProductForScanOrder) => void;
  fontColor?: string;
}) {
  const { currency } = useStoreContext();

  return (
    <List.Item>
      <Flex vertical style={{ flex: 1, gap: 10 }}>
        <Flex style={{ flex: 1 }}>
          <List.Item.Meta
            avatar={<Avatar src={item.cover} />}
            title={<span style={{ color: fontColor }}>{item.title}</span>}
            description={
              <span style={{ color: fontColor }}>
                {currency}
                {toFixedDisplay(item.price?.value)}
              </span>
            }
          />
          <Flex gap={20} align="center">
            <DeleteOutlined
              style={{ color: "red", cursor: "pointer" }}
              onClick={() => onRemoveItems(item)}
            />
          </Flex>
        </Flex>
      </Flex>
    </List.Item>
  );
}
